import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface MetadataRow {
  readonly assetMetadataId: string;
  readonly metaKey: string;
  readonly metaValue: string | null;
  readonly metaType: string;
  readonly metaGroup: string;
  readonly isSearchable: boolean;
  readonly isRequired: boolean;
}

interface CompleteInfo {
  readonly complete: boolean;
  readonly populated: number;
}

const META_GROUPS = ['', 'DESCRIPTIVE', 'TECHNICAL', 'ADMINISTRATIVE', 'RIGHTS', 'EXIF', 'CUSTOM'];
const META_TYPES = ['STRING', 'NUMBER', 'DATE', 'BOOLEAN', 'JSON', 'TAG'];

/**
 * M36 — Asset Metadata Manager (§31). Per-asset descriptive/technical/rights
 * metadata (T489) workbench: load an asset by id, review its metadata entries
 * grouped and typed across the 6 metadata groups, see whether the mandatory
 * pre-approval metadata is complete (BR-05), filter by metadata group, and
 * upsert a metadata key/value. Tenant isolation is enforced server-side (LAW 4).
 */
export function AssetMetadataManager(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [assetId, setAssetId] = useState('');
  const [loadedAssetId, setLoadedAssetId] = useState('');
  const [rows, setRows] = useState<MetadataRow[]>([]);
  const [complete, setComplete] = useState<CompleteInfo | null>(null);
  const [metaGroup, setMetaGroup] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [metaKey, setMetaKey] = useState('');
  const [metaValue, setMetaValue] = useState('');
  const [metaType, setMetaType] = useState(META_TYPES[0]);
  const [upsertGroup, setUpsertGroup] = useState('DESCRIPTIVE');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async (id: string, group: string): Promise<void> => {
    if (!id.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (group) params.set('metaGroup', group);
      const qs = params.toString();
      const [listRes, completeRes] = await Promise.all([
        apiRequest<{ data: MetadataRow[] }>(`/v1/dam/assets/${id}/metadata${qs ? `?${qs}` : ''}`, { token }),
        apiRequest<{ data: CompleteInfo }>(`/v1/dam/assets/${id}/metadata/complete`, { token }),
      ]);
      setRows(listRes.data);
      setComplete(completeRes.data);
      setLoadedAssetId(id);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
      setComplete(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const onLoad = (e: FormEvent): void => {
    e.preventDefault();
    void load(assetId.trim(), metaGroup);
  };

  const onFilter = (group: string): void => {
    setMetaGroup(group);
    if (loadedAssetId) void load(loadedAssetId, group);
  };

  const upsert = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    if (!metaKey.trim()) {
      setActionError(t('m36.metadata.keyReq'));
      return;
    }
    setBusy(true);
    try {
      await apiRequest(`/v1/dam/assets/${loadedAssetId}/metadata`, {
        method: 'POST',
        body: { metaKey: metaKey.trim(), metaValue: metaValue.trim() || undefined, metaType, metaGroup: upsertGroup },
        token,
      });
      setMetaKey('');
      setMetaValue('');
      await load(loadedAssetId, metaGroup);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m36.metadata.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="asset-metadata-manager">
      <h1>{t('m36.metadata.title')}</h1>
      <form aria-label="load-form" onSubmit={onLoad}>
        <label>
          {t('m36.metadata.assetIdLabel')}
          <input aria-label="field-asset-id" value={assetId} onChange={(e) => setAssetId(e.target.value)} placeholder={t('m36.metadata.assetIdPh')} />
        </label>
        <Button variant="primary" type="submit" aria-label="load-metadata">{t('m36.metadata.load')}</Button>
      </form>

      {error && <p role="alert">{error}</p>}

      {loadedAssetId && !error && (
        <>
          <div className="filters">
            <label>
              {t('m36.common.group')}
              <select aria-label="filter-group" value={metaGroup} onChange={(e) => onFilter(e.target.value)}>
                {META_GROUPS.map((g) => <option key={g || 'all'} value={g}>{g || t('m36.common.all')}</option>)}
              </select>
            </label>
          </div>

          {complete && (
            <dl aria-label="metadata-completeness">
              <dt>{t('m36.metadata.completeLabel')}</dt>
              <dd aria-label="meta-complete">{complete.complete ? t('m36.common.yes') : t('m36.common.no')}</dd>
              <dt>{t('m36.metadata.populatedLabel')}</dt>
              <dd aria-label="meta-populated">{complete.populated}</dd>
            </dl>
          )}

          {loading ? <p>{t('common.loading')}</p> : (
            <>
              <p aria-label="metadata-count">{t('m36.metadata.count', { count: rows.length })}</p>
              <DataTable
                ariaLabel="metadata-table"
                rows={rows}
                rowKey={(r) => r.assetMetadataId}
                columns={[
                  { key: 'metaKey', header: t('m36.metadata.col.key'), render: (r) => r.metaKey },
                  { key: 'metaValue', header: t('m36.metadata.col.value'), render: (r) => r.metaValue ?? '' },
                  { key: 'metaType', header: t('m36.common.type'), render: (r) => r.metaType },
                  { key: 'metaGroup', header: t('m36.common.group'), render: (r) => r.metaGroup },
                  { key: 'isSearchable', header: t('m36.metadata.col.searchable'), render: (r) => (r.isSearchable ? t('m36.common.yes') : t('m36.common.no')) },
                  { key: 'isRequired', header: t('m36.metadata.col.required'), render: (r) => (r.isRequired ? t('m36.common.yes') : t('m36.common.no')) },
                ]}
              />

              <form aria-label="upsert-form" onSubmit={(e) => void upsert(e)}>
                <label>
                  {t('m36.metadata.col.key')}
                  <input aria-label="field-key" value={metaKey} onChange={(e) => setMetaKey(e.target.value)} />
                </label>
                <label>
                  {t('m36.metadata.col.value')}
                  <input aria-label="field-value" value={metaValue} onChange={(e) => setMetaValue(e.target.value)} />
                </label>
                <label>
                  {t('m36.common.type')}
                  <select aria-label="field-metatype" value={metaType} onChange={(e) => setMetaType(e.target.value)}>
                    {META_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
                  </select>
                </label>
                <label>
                  {t('m36.common.group')}
                  <select aria-label="field-metagroup" value={upsertGroup} onChange={(e) => setUpsertGroup(e.target.value)}>
                    {META_GROUPS.filter((g) => g).map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </label>
                <Button variant="primary" type="submit" aria-label="save-metadata" disabled={busy}>
                  {busy ? t('m36.metadata.saving') : t('m36.metadata.save')}
                </Button>
              </form>
              {actionError && <p role="alert" aria-label="metadata-error">{actionError}</p>}
            </>
          )}
        </>
      )}
    </main>
  );
}
